import { SupabaseClient } from '@supabase/supabase-js';

export interface OperationalComparison {
  metricName: string;
  average: number;
  median: number;
  percentile25: number;
  percentile75: number;
  unit: string;
  desiredTrend: 'up' | 'down';
  isMonetary: boolean;
}

export interface ComparisonQueryParams {
  region?: string;
  propertyType?: string;
  storeSize?: number;
  storeType?: string;
}

export interface IndustryBenchmark {
  metricName: string;
  nationalAverage: number;
  industryMin: number;
  industryMax: number;
  topPerformerThreshold: number;
  unit: string;
  desiredTrend: 'up' | 'down';
  isMonetary: boolean;
  category: string;
}

/**
 * Fetches operational comparison metrics from the OpComps table
 * 
 * @param supabase - Supabase client instance
 * @param params - Optional parameters to filter comparison data (region, property type, etc.)
 * @returns Array of operational comparison metrics
 */
export async function fetchOperationalComparisons(
  supabase: SupabaseClient,
  params?: ComparisonQueryParams
): Promise<{
  success: boolean;
  data?: OperationalComparison[];
  error?: string;
}> {
  try {
    // Start building the query
    let query = supabase
      .from('private_ai_use.OpComps')
      .select('*');
    
    // Apply filters if provided
    if (params?.region) {
      query = query.eq('region', params.region);
    }
    
    if (params?.propertyType) {
      query = query.eq('property_type', params.propertyType);
    }
    
    if (params?.storeSize) {
      // Find comparisons for similar store sizes (within range)
      const minSize = params.storeSize * 0.7; // 70% of the store size
      const maxSize = params.storeSize * 1.3; // 130% of the store size
      query = query.gte('store_size_min', minSize).lte('store_size_max', maxSize);
    }
    
    if (params?.storeType) {
      query = query.eq('store_type', params.storeType);
    }
    
    // Execute the query
    const { data, error } = await query;
    
    if (error) {
      console.error('Error fetching comparison data:', error);
      return {
        success: false,
        error: error.message
      };
    }
    
    // Transform data to the expected format
    const operationalComparisons: OperationalComparison[] = data.map((item: any) => ({
      metricName: item.metric_name,
      average: item.average_value,
      median: item.median_value,
      percentile25: item.percentile_25,
      percentile75: item.percentile_75,
      unit: item.unit,
      desiredTrend: item.desired_trend,
      isMonetary: item.is_monetary
    }));
    
    return {
      success: true,
      data: operationalComparisons
    };
  } catch (err) {
    console.error('Error in fetchOperationalComparisons:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Unknown error fetching comparison data'
    };
  }
}

/**
 * Fetches industry benchmark data from the Industry_Benchmarks table
 * 
 * @param supabase - Supabase client instance
 * @param params - Optional parameters to filter benchmark data
 * @returns Array of industry benchmark metrics
 */
export async function fetchIndustryBenchmarks(
  supabase: SupabaseClient,
  params?: { category?: string }
): Promise<{
  success: boolean;
  data?: IndustryBenchmark[];
  error?: string;
}> {
  try {
    // Start building the query
    let query = supabase
      .from('private_ai_use.Industry_Benchmarks')
      .select('*');
    
    // Apply filters if provided
    if (params?.category) {
      query = query.eq('category', params.category);
    }
    
    // Execute the query
    const { data, error } = await query;
    
    if (error) {
      console.error('Error fetching industry benchmarks:', error);
      return {
        success: false,
        error: error.message
      };
    }
    
    // Transform data to the expected format
    const benchmarks: IndustryBenchmark[] = data.map((item: any) => ({
      metricName: item.metric_name,
      nationalAverage: item.national_average,
      industryMin: item.industry_min,
      industryMax: item.industry_max,
      topPerformerThreshold: item.top_performer_threshold,
      unit: item.unit,
      desiredTrend: item.desired_trend,
      isMonetary: item.is_monetary,
      category: item.category
    }));
    
    return {
      success: true,
      data: benchmarks
    };
  } catch (err) {
    console.error('Error in fetchIndustryBenchmarks:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Unknown error fetching benchmark data'
    };
  }
}
